import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { del, get, post, put, withPath, type ApiPath } from '@app/api/typed';
import {
  forQuery,
  type Identity,
  type IdentitiesPage,
  type PoliciesFilters,
  type PoliciesPage,
  type Policy,
  type PolicyInput,
  type PolicySummary,
  type RegoValidateResult,
  type Resource,
  type ResourcesPage,
} from './PolicyModel';

/**
 * Hooks de T11/T12 (FR-079…FR-089).
 *
 * Toda escrita invalida lista **e** indicadores: ativar uma política muda os
 * quatro números do topo, e mostrar número velho ao lado de linha nova seria
 * mentira.
 */
export const policiesKeys = {
  todas: ['policies'] as const,
  lista: (filters: PoliciesFilters) => ['policies', 'list', forQuery(filters)] as const,
  resumo: ['policies', 'summary'] as const,
  detail: (id: string) => ['policies', 'detail', id] as const,
  identities: ['policies', 'identities'] as const,
  resources: ['policies', 'resources'] as const,
};

function rota<P extends ApiPath>(template: P, id: string): P {
  return withPath(template, { id }) as P;
}

export function usePolicies(filters: PoliciesFilters): UseQueryResult<PoliciesPage, ApiError> {
  return useQuery<PoliciesPage, ApiError>({
    queryKey: policiesKeys.lista(filters),
    queryFn: async () => {
      const page = await get('/api/v1/policies', { params: forQuery(filters) });
      return { ...page, items: (page.items ?? []) as Policy[] };
    },
    placeholderData: keepPreviousData,
  });
}

/** Indicadores do topo de T11 (FR-087) + se o executor valida Rego (FR-082). */
export function usePoliciesSummary(): UseQueryResult<PolicySummary, ApiError> {
  return useQuery<PolicySummary, ApiError>({
    queryKey: policiesKeys.resumo,
    queryFn: () => get('/api/v1/policies/summary'),
  });
}

export function usePolicy(id: string | null): UseQueryResult<Policy, ApiError> {
  return useQuery<Policy, ApiError>({
    queryKey: policiesKeys.detail(id ?? ''),
    queryFn: () => get(rota('/api/v1/policies/{id}', id as string)),
    enabled: Boolean(id),
  });
}

/** Identidades para a aba **Identities**. */
export function useIdentitiesForPolicy(
  habilitado: boolean,
): UseQueryResult<IdentitiesPage, ApiError> {
  return useQuery<IdentitiesPage, ApiError>({
    queryKey: policiesKeys.identities,
    queryFn: async () => {
      const page = await get('/api/v1/identities', { params: { size: 200 } });
      return { ...page, items: (page.items ?? []) as Identity[] };
    },
    enabled: habilitado,
  });
}

/** Resources com tools para a aba **Capabilities**. */
export function useResourcesForPolicy(
  habilitado: boolean,
): UseQueryResult<ResourcesPage, ApiError> {
  return useQuery<ResourcesPage, ApiError>({
    queryKey: policiesKeys.resources,
    queryFn: async () => {
      const page = await get('/api/v1/resources', { params: { size: 200 } });
      return { ...page, items: (page.items ?? []) as Resource[] };
    },
    enabled: habilitado,
  });
}

function useInvalidarTudo() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: policiesKeys.todas });
}

export type CreatePolicyResult = { data: Policy; message: string };

/** Criar. `estado: ativa` publica no PDP na mesma operação (FR-083). */
export function useCreatePolicy(): UseMutationResult<Policy, ApiError, PolicyInput> {
  const invalidar = useInvalidarTudo();
  return useMutation<Policy, ApiError, PolicyInput>({
    mutationFn: (body) => post('/api/v1/policies', { body }),
    onSuccess: invalidar,
  });
}

export interface UpdatePolicyVars {
  id: string;
  body: PolicyInput;
}

export function useUpdatePolicy(): UseMutationResult<Policy, ApiError, UpdatePolicyVars> {
  const invalidar = useInvalidarTudo();
  return useMutation<Policy, ApiError, UpdatePolicyVars>({
    mutationFn: ({ id, body }) => put(rota('/api/v1/policies/{id}', id), { body }),
    onSuccess: invalidar,
  });
}

export interface ActivateVars {
  id: string;
  version: number;
}

export function useActivatePolicy(): UseMutationResult<Policy, ApiError, ActivateVars> {
  const invalidar = useInvalidarTudo();
  return useMutation<Policy, ApiError, ActivateVars>({
    mutationFn: ({ id, version }) =>
      post(rota('/api/v1/policies/{id}/activate', id), { body: { version } }),
    onSuccess: invalidar,
  });
}

export function useDeactivatePolicy(): UseMutationResult<Policy, ApiError, ActivateVars> {
  const invalidar = useInvalidarTudo();
  return useMutation<Policy, ApiError, ActivateVars>({
    mutationFn: ({ id, version }) =>
      post(rota('/api/v1/policies/{id}/deactivate', id), { body: { version } }),
    onSuccess: invalidar,
  });
}

export type DeleteResult = { affected_identities?: number };

/** Excluir republica o conjunto completo de ativas antes de gravar (FR-085). */
export function useDeletePolicy(): UseMutationResult<DeleteResult, ApiError, string> {
  const invalidar = useInvalidarTudo();
  return useMutation<DeleteResult, ApiError, string>({
    mutationFn: (id) => del(rota('/api/v1/policies/{id}', id)),
    onSuccess: invalidar,
  });
}

export interface ValidateRegoVars {
  rego: string;
  policy_id?: string | null;
}

/**
 * **Validar** (FR-081): confere a **sintaxe** pelo executor.
 *
 * Erro de sintaxe não é erro HTTP — vem em `data.ok` + `data.erros[]`, com linha
 * e coluna. `502 rota_ausente` = middleware sem Rego base (FR-082).
 */
export function useValidateRego(): UseMutationResult<
  RegoValidateResult,
  ApiError,
  ValidateRegoVars
> {
  const queryClient = useQueryClient();
  return useMutation<RegoValidateResult, ApiError, ValidateRegoVars>({
    mutationFn: (body) => post('/api/v1/policies/validate-rego', { body }),
    onSuccess: (_resultado, vars) => {
      // Validar com `policy_id` grava `rego_validado_em` no registro.
      if (vars.policy_id) {
        void queryClient.invalidateQueries({ queryKey: policiesKeys.detail(vars.policy_id) });
      }
    },
  });
}
